import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { FilterBar, defaultFilters, cleanParams } from "@/components/FilterBar";
import { RecordDialog } from "@/components/RecordDialog";
import { ReceiptViewer } from "@/components/Receipt";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { PageBlocks } from "@/components/content/BlockView";
import { useApp } from "@/context/AppContext";
import { api, errMsg, pendingRows, setReportSec } from "@/lib/api";
import { recordConfig } from "@/lib/records";
import { money, fmtDate } from "@/lib/format";
import { useSection } from "@/lib/sections";
import { BulkAssignBar, SelectBox } from "@/components/BulkAssignBar";
import { UnassignedToggle } from "@/components/UnassignedToggle";
import { PendingBadge } from "@/components/PendingBadge";
import { EntryClosed } from "@/components/EntryClosed";

function Cell({ col, row }) {
  const v = col.get ? col.get(row) : row[col.key];
  if (col.fmt === "money") return <span className="font-mono font-semibold">{money(v)}</span>;
  if (col.fmt === "date") return <span className="whitespace-nowrap">{fmtDate(v)}</span>;
  if (col.fmt === "badge") return v ? <span className="whitespace-nowrap rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-900">{v}</span> : null;
  if (col.fmt === "num") return <span className="font-mono">{v ?? 0}</span>;
  if (col.fmt === "long") return <span className="line-clamp-3 block min-w-[14rem] max-w-sm whitespace-normal text-slate-600">{v}</span>;
  return <span>{v}</span>;
}

export default function RecordsPage({ type }) {
  const ctx = useApp();
  const cfg = recordConfig(type, ctx);
  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useState(defaultFilters());
  const [rows, setRows] = useState([]);
  const [dialog, setDialog] = useState({ open: false, record: null });
  const [receipt, setReceipt] = useState(null);
  const [sel, setSel] = useState([]);
  const [unassigned, setUnassigned] = useState(false);
  const section = useSection();
  const sectioned = section.name && (cfg.sideScoped || cfg.fields.some((f) => f.key === "destination"));
  const showUnassigned = ctx.isFullAdmin && cfg.fields.some((f) => f.key === "destination");
  useEffect(() => { if (section.key) { setReportSec(section.key); } }, [section.key]);

  const load = useCallback(() => {
    const p = { ...cleanParams(filters), ...(unassigned && showUnassigned ? { unassigned: true } : sectioned ? { destination: section.name } : {}) };
    const pend = () => pendingRows(cfg.endpoint, ctx.churches, (d) => !sectioned || d.destination === section.name);
    api.get(`/${cfg.endpoint}`, { params: p }).then((r) => setRows([...pend(), ...r.data.filter((x) => !String(x.id).startsWith("offline-"))])).catch((e) => { setRows(pend()); if (navigator.onLine) toast.error(errMsg(e)); });
  }, [cfg.endpoint, filters, sectioned, section.name, unassigned, showUnassigned, ctx.churches]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    window.addEventListener("mwjk-synced", load);
    window.addEventListener("mwjk-queue", load);
    return () => { window.removeEventListener("mwjk-synced", load); window.removeEventListener("mwjk-queue", load); };
  }, [load]);
  useEffect(() => {
    if (params.get("new") === "1") { if (canAdd) { setDialog({ open: true, record: null }); } const next = new URLSearchParams(params); next.delete("new"); setParams(next, { replace: true }); }
  }, [params, setParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (id) => {
    try { await api.delete(`/${cfg.endpoint}/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const open = (sk) => (ctx.isAdmin ? ctx.hasArea("records") : !!sk && ctx.can(`church_entry_${type}_${sk}`));
  const secOf = (d) => (d === "General Council" ? "gc" : d === "MWJK" ? "mwjk" : section.key);
  const canAdd = open(section.key);
  const canEdit = ctx.canEditRec && (ctx.isAdmin ? ctx.hasArea("records") : canAdd);
  const rowOpen = (r) => (ctx.isAdmin ? canAdd : !!section.key && open(secOf(r.destination)));
  const canDelete = ctx.isAdmin ? ctx.hasArea("records") : ctx.can("church_can_delete");
  const anyAction = (canEdit || canDelete) && (ctx.isAdmin || canAdd);
  const canBulk = cfg.fields.some((f) => f.key === "destination") && ctx.canBulk && (!ctx.isAdmin || ctx.hasArea("records"));
  const toggle = (id, on) => setSel((s) => (on ? [...s, id] : s.filter((x) => x !== id)));
  const cols = cfg.columns.filter((c) => (c.key !== "church_name" || ctx.isAdmin) && (!c.when || c.when(ctx)));
  const order = new Map(ctx.churches.map((c, i) => [c.id, i]));
  const list = cfg.groupByChurch ? [...rows].sort((a, b) => (order.get(a.church_id) ?? 1e9) - (order.get(b.church_id) ?? 1e9)) : rows;
  const total = cfg.moneyKey ? rows.reduce((a, r) => a + Number(r[cfg.moneyKey] || 0), 0) : null;

  return (
    <div>
      <PageHeader eyebrow={[section.name, ctx.isAdmin ? "All churches" : ctx.myChurch?.name].filter(Boolean).join(" · ")} title={type === "progress" && section.name ? `${section.name} Progress Report` : cfg.title} subtitle={sectioned ? `Showing records for ${section.name} only` : cfg.subtitle}>
        {canAdd && <Button size="lg" onClick={() => setDialog({ open: true, record: null })} data-testid={`${cfg.endpoint}-add-btn`}>
          <Plus className="mr-2 h-5 w-5" /> Add {cfg.singular}
        </Button>}
      </PageHeader>
      <PageBlocks page={type} />
      {!ctx.isAdmin && !canAdd && <EntryClosed what={cfg.singular} noSide={!section.key} testid={`${cfg.endpoint}-entry-closed`} />}
      <FilterBar value={filters} onChange={setFilters} extra={sectioned ? cfg.filters.filter((f) => f.key !== "destination") : cfg.filters} testid={`${cfg.endpoint}-filters`} />
      <div className="text-on-bg-muted mt-6 flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
        {showUnassigned && <UnassignedToggle on={unassigned} onChange={(v) => { setUnassigned(v); setSel([]); }} count={rows.length} testid={`${cfg.endpoint}-unassigned-toggle`} />}
        <span data-testid={`${cfg.endpoint}-count`}><b className="text-on-bg">{rows.length}</b> records</span>
        {total !== null && <span data-testid={`${cfg.endpoint}-total`}>Total: <b className="text-on-bg font-mono text-lg">{money(total)}</b></span>}
      </div>
      {canBulk && <BulkAssignBar kind={cfg.endpoint} ids={sel.filter((id) => rows.some((r) => r.id === id))} onClear={() => setSel([])} onDone={load} />}
      <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid={`${cfg.endpoint}-table`}>
          <thead>
            <tr className="border-b bg-[hsl(var(--sand))] text-left">
              {canBulk && <th className="w-10 px-4 py-3"><SelectBox checked={rows.some((r) => !r._pending) && rows.filter((r) => !r._pending).every((r) => sel.includes(r.id))} onChange={(on) => setSel(on ? rows.filter((r) => !r._pending).map((r) => r.id) : [])} label="Select all" testid={`${cfg.endpoint}-select-all`} /></th>}
              {cols.map((c) => (
                <th key={c.key} className={`px-4 py-3 font-semibold text-slate-700 ${c.fmt === "money" || c.fmt === "num" ? "text-right" : ""}`}>
                  <div className="whitespace-nowrap">{c.label}</div>
                  {c.sub && <div className="max-w-40 text-[11px] font-normal leading-tight text-slate-500">{c.sub}</div>}
                </th>
              ))}
              <th className="sticky right-0 bg-[hsl(var(--sand))] px-4 py-3 text-right font-semibold text-slate-700 shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.15)]">{anyAction ? "Actions" : "Receipt"}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={cols.length + (canBulk ? 2 : 1)} className="px-4 py-12 text-center text-slate-500" data-testid={`${cfg.endpoint}-empty`}>No records yet for these filters.{canAdd ? ` Tap “Add ${cfg.singular}” to start.` : ""}</td></tr>
            )}
            {list.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 transition-colors hover:bg-slate-50" data-testid={`${cfg.endpoint}-row-${r.id}`}>
                {canBulk && <td className="px-4 py-3">{!r._pending && <SelectBox checked={sel.includes(r.id)} onChange={(on) => toggle(r.id, on)} label="Select record" testid={`${cfg.endpoint}-select-${r.id}`} />}</td>}
                {cols.map((c, ci) => (
                  <td key={c.key} className={`px-4 py-3 ${c.fmt === "money" || c.fmt === "num" ? "text-right" : ""}`}><Cell col={c} row={r} />{ci === 0 && r._pending && <PendingBadge testid={`${cfg.endpoint}-pending-${r.id}`} />}</td>
                ))}
                <td className="sticky right-0 bg-white px-4 py-2 shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.15)]">
                  <div className="flex justify-end gap-1">
                    {(r.receipt_ids?.length > 0 || r.receipt_id) && (
                      <Button variant="ghost" size="icon" className="relative" onClick={() => setReceipt(r.receipt_ids?.length ? r.receipt_ids : r.receipt_id)} aria-label="View attached files" data-testid={`${cfg.endpoint}-receipt-${r.id}`}>
                        <Paperclip className="h-4 w-4 text-[hsl(var(--emerald))]" />
                        {r.receipt_ids?.length > 1 && <span className="absolute -right-0.5 -top-0.5 rounded-full bg-[hsl(var(--emerald))] px-1 text-[10px] font-bold leading-4 text-white" data-testid={`${cfg.endpoint}-file-count-${r.id}`}>{r.receipt_ids.length}</span>}
                      </Button>
                    )}
                    {canEdit && rowOpen(r) && !r._pending && (
                      <Button variant="ghost" size="icon" onClick={() => setDialog({ open: true, record: r })} aria-label="Edit" data-testid={`${cfg.endpoint}-edit-${r.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    {canDelete && rowOpen(r) && !r._pending && <ConfirmDelete onConfirm={() => remove(r.id)} title={`Delete this ${cfg.singular.toLowerCase()}?`} testid={`${cfg.endpoint}-delete-${r.id}`} />}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <RecordDialog cfg={cfg} preset={sectioned ? { destination: section.name } : null} open={dialog.open} record={dialog.record} onClose={() => setDialog({ open: false, record: null })}
        onSaved={() => { setDialog({ open: false, record: null }); load(); }} />
      <ReceiptViewer id={receipt} onClose={() => setReceipt(null)} />
    </div>
  );
}
